import {
    Device,
    DeviceType,
    PinConfig,
    TibboBasicEnum,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import EM510W_enums from './boards/tibbo/EM510W/EM510W_enums.json';
import EM2000W_enums from './boards/tibbo/EM2000W/EM2000W_enums.json';
import WM2000_enums from './boards/tibbo/WM2000/WM2000_enums.json';
import TPP2G2_enums from './boards/tibbo/TPP2W(G2)/TPP2W(G2)_enums.json';
import TPP3G2_enums from './boards/tibbo/TPP3W(G2)/TPP3W(G2)_enums.json';
import LTPP3_enums from './boards/tibbo/LTPP3/LTPP3_enums.json';
import LTPP3G2_enums from './boards/tibbo/LTPP3G2/LTPP3G2_enums.json';
import WS1102_enums from './boards/tibbo/WS1102/WS1102_enums.json';
import EM2000W_pins from './boards/tibbo/EM2000W/EM2000W_pins.json';
import WS1102_pins from './boards/tibbo/WS1102/WS1102_pins.json';
import WM2000_pins from './boards/tibbo/WM2000/WM2000_pins.json';
import WM2000EV_pins from './boards/tibbo/WM2000EV/WM2000EV_pins.json';
import EM510W_pins from './boards/tibbo/EM510W/EM510W_pins.json';

import ESP32DEVKITC from './ESP32DEVKITC/board.json';
import nrf52dk_nrf52840 from './nrf52dk_nrf52840/board.json';
import nrf52dk_nrf52832 from './nrf52dk_nrf52832/board.json';

import ALL_CAPABILITIES from './capabilities.json';

import ALL_BOARDS from './boards/boards.json';

type EnumCollectionType = {
    name: string;
    enums: Array<TibboBasicEnum>
};

export const DeviceDefinitions: { [id: string]: any; } = {};

const EnumsMap: { [platformId: string]: Array<EnumCollectionType> } = {
    EM510W: EM510W_enums,
    EM2000W: EM2000W_enums,
    WM2000: WM2000_enums,
    'TPP3W(G2)': TPP3G2_enums,
    'TPP2W(G2)': TPP2G2_enums,
    LTPP3: LTPP3_enums,
    LTPP3G2: LTPP3G2_enums,
    WS1102: WS1102_enums,
};

export const BOARD_FILES: { [id: string]: string; } = {
    'TPP2W(G2)': 'tpp2g2',
    'TPP3W(G2)': 'tpp3g2',
    LTPP3: 'ltpp3',
    LTPP3G2: 'ltpp3g2',
    EM2000W: 'EM2000W',
    WM2000: 'WM2000',
    EM510W: 'EM510W',
    WM2000EV: 'WM2000EV',
    WS1102: 'WS1102',
};

export enum TIBBO_DEVICE_ID {
    TPP2WG2 = 'TPP2W(G2)',
    TPP3WG2 = 'TPP3W(G2)',
    LTPP3 = 'LTPP3',
    LTPP3G2 = 'LTPP3G2',
    EM2000 = 'EM2000',
    EM2000W = 'EM2000W',
    WM2000 = 'WM2000',
    EM510W = 'EM510W',
    WM2000EV = 'WM2000EV',
    WS1102 = 'WS1102',
    RPPICOW = 'RPPICOW',
    RPPICO = 'RPPICO',
    RPPICOSIMULATOR = 'RPPICOSIMULATOR',
    M5STACKCORE2 = 'm5stack_core2',
    ESP32DEVKITC = 'esp32_devkitc_wroom',
    nrf52dk_nrf52832 = 'nrf52dk_nrf52832',
    DS1102N = 'DS1102N',
    DS1101N = 'DS1101N',
}

export enum RUNTIMES {
    TIOS = 'tios',
    ARDUINO = 'arduino',
    MICROPYTHON = 'micropython',
    ZEPHYR = 'zephyr',
    NTIOS = 'ntios',
    SIMULATOR = 'simulator',
}

export const RESERVED_KEYWORDS: { [runtime: string]: string[] } = {
    [RUNTIMES.TIOS]: [
        'and', 'as', 'boolean', 'byref', 'byte', 'byval', 'case', 'char', 'const', 'countof',
        'declare', 'dim', 'do', 'doevents', 'dword', 'else', 'end', 'enum',
        'false', 'float', 'for', 'function', 'get', 'goto', 'if', 'include', 'includepp', 'integer',
        'long', 'loop', 'mod', 'next', 'not', 'object', 'or', 'property', 'public', 'real', 'ref',
        'select', 'set', 'shl', 'short', 'shr', 'sizeof', 'step', 'string', 'sub', 'syscall',
        'then', 'to', 'true', 'type', 'until', 'wend', 'while', 'word', 'xor',
    ],
    [RUNTIMES.MICROPYTHON]: [
        'and', 'as', 'assert', 'break', 'class', 'continue',
        'def', 'del', 'elif', 'else', 'except', 'exec',
        'finally', 'for', 'from', 'global', 'if', 'import',
        'in', 'is', 'lambda', 'nonlocal', 'not', 'or',
        'pass', 'raise', 'return', 'try', 'while', 'with',
        'yield', 'True', 'False', 'None',
    ],
    [RUNTIMES.ARDUINO]: [],
    [RUNTIMES.ZEPHYR]: [
        'asm', 'double', 'new', 'switch',
        'auto', 'else', 'operator', 'template',
        'break', 'enum', 'private', 'this',
        'case', 'extern', 'protected', 'throw',
        'catch', 'float', 'public', 'try',
        'char', 'for', 'register', 'typedef',
        'class', 'friend', 'return', 'union',
        'const', 'goto', 'short', 'unsigned',
        'continue', 'if', 'signed', 'virtual',
        'default', 'inline', 'sizeof', 'void',
        'delete', 'int', 'static', 'volatile',
        'do', 'long', 'struct', 'while',
    ],
    [RUNTIMES.SIMULATOR]: [ // javascript
        'let', 'const', 'var',
        'if', 'else', 'for', 'while', 'do',
        'break', 'continue', 'return', 'switch',
        'case', 'default', 'try', 'catch',
        'class', 'extends', 'constructor',
        'async', 'await', 'function',
        'import', 'export', 'from',
        'new', 'delete', 'typeof',
        'this', 'super', 'static',
        'void', 'null', 'undefined',
    ],
    [RUNTIMES.NTIOS]: [],
};

export const DEVICE_PINS: { [id: string]: PinConfig[]; } = {
    EM2000W: EM2000W_pins,
    EM2000: EM2000W_pins,
    WM2000: WM2000_pins,
    WS1102: WS1102_pins,
    EM510W: EM510W_pins,
    WM2000EV: WM2000EV_pins,
    [TIBBO_DEVICE_ID.ESP32DEVKITC]: ESP32DEVKITC.pins,
    [TIBBO_DEVICE_ID.nrf52dk_nrf52832]: nrf52dk_nrf52832.pins,
};

export const DEVICE_TYPES: Device[] = [
    {
        id: 'EM2000W',
        name: 'EM2001',
        platform: 'EM2000W',
        image: '/static/img/em2001-iso-300.jpg',
        deviceType: DeviceType.BOARD,
        runtimes: [RUNTIMES.TIOS],
        capabilities: ['ethernet', 'network'],
        description: `
Our flagship BASIC/C-programmable IoT module offering significant performance, memory size, and feature improvements over its predecessor — the original EM1000 device. Ethernet, as well as optional Wi-Fi and GPRS connectivity. Four serial ports supporting full-/half-duplex RS232, Wiegand, and clock/data modes.

Flash disk and RTC. 56 I/O lines for connecting LCD, keypad, buzzer, and other devices.
    
Just the module you need to get your IIoT project going.`,
    },
    {
        id: 'EM2000',
        name: 'EM2000',
        platform: 'EM2000W',
        image: '/static/img/em2000.jpg',
        deviceType: DeviceType.BOARD,
        runtimes: [RUNTIMES.TIOS],
        capabilities: ['ethernet', 'network'],
        description: `
The EM2000 is Tibbo's fastest, as well as the most powerful and versatile BASIC/C-programmable IoT module. The module is a high-performance upgrade to our popular EM1000 device.

The module's hardware features include 100/10BaseT Ethernet port, four serial ports supporting UART, Wiegand, and clock/data modes, four I2C/SPI ports, onboard flash, EEPROM, RTC, and up to 56 I/O lines.
        
The EM2000 is also excellent for prototyping your projects — it has the standard pin pitch of 2.54mm (0.1").`,
    },
    {
        id: 'EM510W',
        name: 'EM510',
        platform: 'EM510W',
        image: '/static/img/em510-iso-300.jpg',
        deviceType: DeviceType.BOARD,
        runtimes: [RUNTIMES.TIOS],
        capabilities: ['ethernet', 'network'],
        description: `
Being the smallest among our BASIC/C-programmable modules, the "vertical slice" EM510 hides behind a standard RJ45 jack and occupies only 18.5 x 6.5mm (0.73 x 0.25") of board space.

Ethernet and optional Wi-Fi/BLE (Bluetooth Low Energy) connectivity. One serial port supporting full-/half-duplex RS232, Wiegand, and clock/data modes. 10 I/O lines.
    
The EM510 is an upgrade to our original EM500 MiniMo device. This is your economical choice for building ultra-light IoT devices.`,
    },
    {
        id: 'WM2000',
        name: 'WM2000',
        platform: 'WM2000',
        image: '/static/img/wm2000.png',
        deviceType: DeviceType.BOARD,
        runtimes: [RUNTIMES.TIOS],
        capabilities: ['wifi', 'network', 'bluetooth', 'rtc'],
        description: `
The WM2000 is the first programmable wireless IIoT module offered by Tibbo. The device incorporates Wi-Fi and Bluetooth Low Energy (BLE) interfaces that introduce several new features, such as Wi-Fi auto-connects (automatic association), wireless debugging, over-the-air (OTA) updates, and Transport Layer Security (TLS).

The WM2000's hardware incorporates two high-speed serial ports. It also features software I²C/SPI ports, onboard flash and EEPROM, a real-time clock, and ten GPIO lines, two of which work as external interrupts. Nine lines can be configured to provide Pulse-width Modulation (PWM) output.`,
    },
    {
        id: 'WM2000EV',
        name: 'WM2000EV',
        platform: 'WM2000',
        image: '/static/img/wm2000ev.jpg',
        deviceType: DeviceType.BOARD,
        runtimes: [RUNTIMES.TIOS],
        capabilities: ['wifi', 'network', 'bluetooth', 'rtc'],
        description: `
The WM2000EV is a board for evaluating the WM2000 Programmable Wireless IIoT Module, which is included with the board and is preloaded with a special evaluation app. Also included in the accompanying "kit" are a USB Type-C-to-A cable, a CR2032 button cell battery, and four jumpers installed in their default positions.`,
    },
    {
        id: 'WS1102',
        name: 'WS1102',
        platform: 'WS1102',
        image: '/static/img/ws1102.jpg',
        deviceType: DeviceType.BOARD,
        runtimes: [RUNTIMES.TIOS],
        capabilities: ['wifi', 'network', 'bluetooth', 'rtc'],
        description: `
The WS1102 is a compact Tibbo BASIC/C-programmable wireless controller equipped with an RS232/422/485 serial port. The product targets serial-over-IP (SoI) and serial control applications.

This cloud-native device incorporates Wi-Fi (802.11a/b/g/n over 2.4GHz/5GHz) and Bluetooth Low Energy (BLE) interfaces that introduce several new features, such as Wi-Fi auto-connects, wireless debugging, over-the-air (OTA) updates, and Transport Layer Security (TLS) support. As a vendor-agnostic product, it can communicate with Microsoft Azure, Google Cloud, Amazon Web Services (AWS), and virtually any other cloud services provider.`,
    },
    // {
    //     id: "LTPP3",
    //     name: "Linux TPP 2",
    //     description: "",
    //     image: '/static/img/ltpp3-300.jpg',
    //     deviceType: "TPP",
    // },
    {
        id: 'LTPP3G2',
        name: 'Linux TPP 3 Gen 2',
        platform: 'LTPP3(G2)',
        image: '/static/img/ltpp3g2-300.jpg',
        runtimes: [RUNTIMES.ZEPHYR],
        description: `
The Size 3 Linux Tibbo Project PCB (LTPP3) Gen. 2 is a comprehensive reimagining of the original LTPP3 concept. Commonly used features and interfaces are now directly integrated onto the board, creating a more complete, efficient, and refined solution.
        `,
        deviceType: DeviceType.TPP,
    },
];

for (let i = 0; i < ALL_BOARDS.length; i++) {
    try {
        const supportedBoard = ALL_BOARDS[i];
        const deviceId = supportedBoard.id;
        // const isBrowser = () => ![typeof window, typeof document].includes('undefined');
        let appDir;
        if (process.env.BROWSER === undefined) {
            // eslint-disable-next-line global-require
            const fs = require('fs');
            // eslint-disable-next-line global-require
            const path = require('path');
            if (!appDir) {
                appDir = path.resolve(__dirname, '..', '..');
                const appDirParts = appDir.split(path.sep);
                if (appDirParts[appDirParts.length - 2] === 'dist') {
                    appDir = path.resolve(__dirname, '..', '..');
                }
            }

            const board = JSON.parse(fs.readFileSync(path.join(appDir, 'packages', `${supportedBoard.config}`), 'utf8'));
            DeviceDefinitions[deviceId] = board;
        } else {
            DeviceDefinitions[deviceId] = supportedBoard;
        }
    } catch (ex) {
        console.log(ex);
    }
}


Object.keys(DeviceDefinitions).forEach((key) => {
    let found = false;
    const device = DeviceDefinitions[key];
    for (let i = 0; i < DEVICE_TYPES.length; i += 1) {
        if (DEVICE_TYPES[i].id === device.id) {
            found = true;
            break;
        }
    }
    if (!found) {
        DEVICE_TYPES.push({
            id: device.id,
            name: device.name,
            platform: device.platform,
            image: device.image,
            deviceType: device.deviceType,
            runtimes: device.runtimes,
            description: device.description,
            meta: device.meta,
        });
    }
    DEVICE_PINS[key] = device.pins;
});

export const TIBBO_DATATYPE_BOUNDS: { [datatype: string]: { min?: number, max?: number } } = {
    integer: {
        min: -32768,
        max: 32767,
    },
    short: {
        min: -32768,
        max: 32767,
    },
    byte: {
        min: 0,
        max: 255,
    },
    word: {
        min: 0,
        max: 65535,
    },
    dword: {
        min: 0,
        max: 4294967295,
    },
    boolean: {
        min: 0,
        max: 1,
    },
    char: {
        min: -128,
        max: 127,
    },
    long: {
        min: -2147483648,
        max: 2147483647,
    },
    real: {
        // min: -3.402823e38,
        // max: 3.402823e38,
    },
    float: {
        // min: -3.402823e38,
        // max: 3.402823e38,
    },
};

export default {
    getEnum(enumValue: string | undefined, enumType: string, platformId: string): string {
        if (enumValue === undefined) {
            return '';
        }
        const enumsList = EnumsMap[platformId];
        if (enumsList === undefined) {
            return enumValue;
        }
        for (let i = 0; i < enumsList.length; i += 1) {
            if (enumsList[i].name === enumType) {
                for (let j = 0; j < enumsList[i].enums.length; j += 1) {
                    if (enumsList[i].enums[j].value.toString() === enumValue.toString()) {
                        return enumsList[i].enums[j].enum;
                    }
                }
            }
        }
        return '';
    },
    getEnumValue(enumValue: string | undefined, enumType: string, platformId: string): string {
        if (enumValue === undefined) {
            return '';
        }
        const enumsList = EnumsMap[platformId];
        if (enumsList === undefined) {
            return enumValue;
        }
        for (let i = 0; i < enumsList.length; i += 1) {
            if (enumsList[i].name === enumType) {
                for (let j = 0; j < enumsList[i].enums.length; j += 1) {
                    if (enumsList[i].enums[j].value.toString() === enumValue.toString()) {
                        return enumsList[i].enums[j].value;
                    }
                }
            }
        }
        return '';
    },

    getPeripheral(deviceId: string, pin: string, peripheral: string): any | undefined {
        const pins = DeviceDefinitions[deviceId].pins;
        if (pins === undefined) {
            return undefined;
        }
        for (let i = 0; i < pins.length; i += 1) {
            if (pins[i].num === pin) {
                for (let j = 0; j < pins[i].peripherals.length; j += 1) {
                    if (pins[i].peripherals[j].peripheralType === peripheral) {
                        return pins[i].peripherals[j];
                    }
                }
            }
        }
        return undefined;
    },

    getDeviceDefinition(deviceId: string): any | undefined {
        if (DeviceDefinitions[deviceId] !== undefined) {
            return DeviceDefinitions[deviceId];
        }
        if (process.env.BROWSER === undefined) {
            // eslint-disable-next-line global-require
            const device: any = ALL_BOARDS.find(board => board.id === deviceId);
            if (device) {
                return device;
            }
            let appDir;
            // eslint-disable-next-line global-require
            const fs = require('fs');
            // eslint-disable-next-line global-require
            const path = require('path');
            if (!appDir) {
                appDir = path.resolve(__dirname, '..', '..');
                const appDirParts = appDir.split(path.sep);
                if (appDirParts[appDirParts.length - 2] === 'dist') {
                    appDir = path.resolve(__dirname, '..', '..');
                }
            }

            const board = JSON.parse(fs.readFileSync(path.join(appDir, 'packages', `${device?.config}`), 'utf8'));
            DeviceDefinitions[deviceId] = board;
            return board;
        }
        return undefined;
    },

    getDevice(platformId: string): Device | undefined {
        return this.getDeviceDefinition(platformId);
    },

    getRuntimeCodeLocationStart(runtime: string): string {
        if (runtime === 'arduino') {
            return '/****';
        }
        if (runtime === 'micropython') {
            return '#****';
        }
        if (runtime === 'zephyr') {
            return '/****';
        }
        if (runtime === 'simulator') {
            return '/****';
        }
        return '\'****';
    },

    getRuntimeCodeLocationEnd(runtime: string): string {
        if (runtime === 'arduino') {
            return '****/';
        }
        if (runtime === 'micropython') {
            return '****#';
        }
        if (runtime === 'zephyr') {
            return '****/';
        }
        if (runtime === 'simulator') {
            return '****/';
        }
        return '****\'';
    },

    getCStringExpression(expression: string): string {
        if (expression.indexOf('"') === -1) {
            return `${expression}.c_str()`;
        }
        return expression;
    },

    convertDataType(
        expression: string,
        sourceDataType: TibboDataType,
        expectedDataType: TibboDataType,
        runtime: string,
        timeFormat: string = 'hh:mm:ss tt',
    ): string {
        if (runtime === 'simulator') {
            switch (expectedDataType) {
                case TibboDataType.STRING:
                    switch (sourceDataType) {
                        case TibboDataType.CHAR:
                        case TibboDataType.BYTE:
                        case TibboDataType.SHORT:
                        case TibboDataType.BOOLEAN:
                        case TibboDataType.WORD:
                            return `String(${expression})`;
                        case TibboDataType.LONG:
                        case TibboDataType.DWORD:
                            return `String(${expression})`;
                        case TibboDataType.FLOAT:
                        case TibboDataType.REAL:
                        case TibboDataType.INTEGER:
                            return `String(${expression})`;
                        case TibboDataType.DATETIME:
                            if (timeFormat === '') {
                                return `String(${expression})`;
                            }
                            return `formatTimestamp(${expression},"YYYY-MM-DD ${timeFormat}")`;
                        case TibboDataType.TIME:
                            if (timeFormat === '') {
                                return `String(${expression})`;
                            }
                            return `formatTimestamp(${expression},"${timeFormat}")`;
                        default:
                            return expression;
                    }
                case TibboDataType.CHAR:
                case TibboDataType.BYTE:
                case TibboDataType.WORD:
                case TibboDataType.INTEGER:
                case TibboDataType.DWORD:
                case TibboDataType.LONG:
                case TibboDataType.FLOAT:
                case TibboDataType.REAL:
                    return `Number(${expression})`;
                case TibboDataType.DATETIME:
                    switch (sourceDataType) {
                        case TibboDataType.TIME:
                            return `datetime_from_time(${expression}) * 60 + (${expression} MOD 60)`;
                        case TibboDataType.STRING:
                            return `Number(${expression})`;
                        default:
                            return expression;
                    }
                case TibboDataType.TIME:
                    switch (sourceDataType) {
                        case TibboDataType.DATETIME:
                            return `datetime_timestamp_seconds(${expression})`;
                        case TibboDataType.STRING:
                            return `Number(${expression})`;
                        default:
                            return expression;
                    }
                default:
                    return expression;
            }
        }
        if (runtime === 'micropython') {
            switch (expectedDataType) {
                case TibboDataType.STRING:
                    switch (sourceDataType) {
                        case TibboDataType.CHAR:
                        case TibboDataType.BYTE:
                        case TibboDataType.SHORT:
                        case TibboDataType.BOOLEAN:
                        case TibboDataType.WORD:
                            return `str(${expression})`;
                        case TibboDataType.LONG:
                        case TibboDataType.DWORD:
                            return `str(${expression})`;
                        case TibboDataType.FLOAT:
                        case TibboDataType.REAL:
                        case TibboDataType.INTEGER:
                            return `str(${expression})`;
                        case TibboDataType.DATETIME:
                            if (timeFormat === '') {
                                return `str(${expression})`;
                            }
                            return `datetime_timestamp_to_string(${expression},"YYYY-MM-DD ${timeFormat})")`;
                        case TibboDataType.TIME:
                            if (timeFormat === '') {
                                return `str(${expression})`;
                            }
                            return `datetime_timestamp_to_string(datetime_mins_to_timestamp(${expression} / 60) + ${expression} MOD 60,"${timeFormat}")`;
                        default:
                            return expression;
                    }
                case TibboDataType.DATETIME:
                    switch (sourceDataType) {
                        case TibboDataType.TIME:
                            return `datetime_mins_to_timestamp(${expression}) * 60 + (${expression} MOD 60)`;
                        default:
                            return expression;
                    }
                    break;
                case TibboDataType.TIME:
                    switch (sourceDataType) {
                        case TibboDataType.DATETIME:
                            return `datetime_timestamp_seconds(${expression})`;
                        default:
                            return expression;
                    }
                case TibboDataType.CHAR:
                case TibboDataType.BYTE:
                case TibboDataType.WORD:
                case TibboDataType.INTEGER:
                    switch (sourceDataType) {
                        case TibboDataType.STRING:
                            return `int(${expression})`;
                        default:
                            return expression;
                    }
                case TibboDataType.BOOLEAN:
                    switch (sourceDataType) {
                        case TibboDataType.STRING:
                            return `(val(${expression})<>0)`;
                        default:
                            return `(${expression}<>0)`;
                    }
                case TibboDataType.DWORD:
                case TibboDataType.LONG:
                    switch (sourceDataType) {
                        case TibboDataType.STRING:
                            return `int(${expression})`;
                        default:
                            return expression;
                    }
                case TibboDataType.FLOAT:
                case TibboDataType.REAL:
                    switch (sourceDataType) {
                        case TibboDataType.STRING:
                            return `float(${expression})`;
                        default:
                            return expression;
                    }
                default:
                    return expression;
            }
        }
        if (runtime === 'zephyr') {
            switch (expectedDataType) {
                case TibboDataType.STRING:
                    switch (sourceDataType) {
                        case TibboDataType.CHAR:
                        case TibboDataType.BYTE:
                            return `to_string(${expression})`;
                        case TibboDataType.SHORT:
                        case TibboDataType.BOOLEAN:
                        case TibboDataType.WORD:
                            return `str(${expression})`;
                        case TibboDataType.LONG:
                        case TibboDataType.DWORD:
                            return `str(${expression})`;
                        case TibboDataType.REAL:
                        case TibboDataType.INTEGER:
                            return `to_string(${expression})`;
                        case TibboDataType.FLOAT:
                            return `shortest_float(${expression})`;
                        case TibboDataType.DATETIME:
                            if (timeFormat === '') {
                                return `to_string(${expression})`;
                            }
                            return `datetime_timestamp_to_string(${expression},"YYYY-MM-DD ${timeFormat})")`;
                        case TibboDataType.TIME:
                            if (timeFormat === '') {
                                return `to_string(${expression})`;
                            }
                            return `datetime_timestamp_to_string(datetime_mins_to_timestamp(${expression} / 60) + ${expression} MOD 60,"${timeFormat}")`;
                        default:
                            return expression;
                    }
                case TibboDataType.DATETIME:
                    switch (sourceDataType) {
                        case TibboDataType.TIME:
                            return `datetime_mins_to_timestamp(${expression}) * 60 + (${expression} MOD 60)`;
                        default:
                            return expression;
                    }
                case TibboDataType.TIME:
                    switch (sourceDataType) {
                        case TibboDataType.DATETIME:
                            return `datetime_timestamp_seconds(${expression})`;
                        default:
                            return expression;
                    }
                case TibboDataType.CHAR:
                case TibboDataType.BYTE:
                case TibboDataType.WORD:
                case TibboDataType.INTEGER:
                    switch (sourceDataType) {
                        case TibboDataType.STRING: {
                            return `strtol(${this.getCStringExpression(expression)}, NULL, 10)`;
                        }
                        case TibboDataType.FLOAT:
                            return `(int)${expression}`;
                        default:
                            return expression;
                    }
                case TibboDataType.BOOLEAN:
                    switch (sourceDataType) {
                        case TibboDataType.STRING:
                            return `(strtol(${expression}, NULL, 10)!=0)`;
                        default:
                            return `(${expression}!=0)`;
                    }
                case TibboDataType.DWORD:
                case TibboDataType.LONG:
                    switch (sourceDataType) {
                        case TibboDataType.STRING: {
                            return `atoi(${this.getCStringExpression(expression)})`;
                        }
                        default:
                            return expression;
                    }
                case TibboDataType.FLOAT:
                case TibboDataType.REAL:
                    switch (sourceDataType) {
                        case TibboDataType.STRING: {
                            return `atof(${this.getCStringExpression(expression)})`;
                        }
                        default:
                            return expression;
                    }
                default:
                    return expression;
            }
        }
        switch (expectedDataType) {
            case TibboDataType.STRING:
                switch (sourceDataType) {
                    case TibboDataType.CHAR:
                    case TibboDataType.BYTE:
                    case TibboDataType.SHORT:
                    case TibboDataType.BOOLEAN:
                    case TibboDataType.WORD:
                    case TibboDataType.INTEGER:
                        return `str(${expression})`;
                    case TibboDataType.LONG:
                    case TibboDataType.DWORD:
                        return `lstr(${expression})`;
                    case TibboDataType.FLOAT:
                    case TibboDataType.REAL:
                        return `ftofixed(${expression},4)`;
                    case TibboDataType.DATETIME:
                        if (timeFormat === '') {
                            return `lstr(${expression})`;
                        }
                        return `datetime_timestamp_to_string(${expression},"YYYY-MM-DD ${timeFormat})")`;
                    case TibboDataType.TIME:
                        if (timeFormat === '') {
                            return `str(${expression})`;
                        }
                        return `datetime_timestamp_to_string(datetime_mins_to_timestamp(${expression} / 60) + ${expression} MOD 60,"${timeFormat}")`;
                    default:
                        return expression;
                }
            case TibboDataType.DATETIME:
                switch (sourceDataType) {
                    case TibboDataType.TIME:
                        return `datetime_mins_to_timestamp(${expression}) * 60 + (${expression} MOD 60)`;
                    default:
                        return expression;
                }
                break;
            case TibboDataType.TIME:
                switch (sourceDataType) {
                    case TibboDataType.DATETIME:
                        return `datetime_timestamp_seconds(${expression})`;
                    default:
                        return expression;
                }
            case TibboDataType.CHAR:
            case TibboDataType.BYTE:
            case TibboDataType.WORD:
                switch (sourceDataType) {
                    case TibboDataType.STRING:
                        return `val(${expression})`;
                    default:
                        return expression;
                }
            case TibboDataType.BOOLEAN:
                switch (sourceDataType) {
                    case TibboDataType.STRING:
                        return `(val(${expression})<>0)`;
                    default:
                        return `(${expression}<>0)`;
                }
            case TibboDataType.DWORD:
            case TibboDataType.LONG:
                switch (sourceDataType) {
                    case TibboDataType.STRING:
                        return `lval(${expression})`;
                    default:
                        return expression;
                }
            case TibboDataType.FLOAT:
            case TibboDataType.REAL:
                switch (sourceDataType) {
                    case TibboDataType.STRING:
                        return `string_to_float(${expression})`;
                    case TibboDataType.BOOLEAN:
                        return `val(str(${expression}))`;
                    default:
                        return expression;
                }
            default:
                return expression;
        }
    },

    getCapabilities(project: any): string[] {
        let capabilities: string[] = [];
        let deviceId = '';
        if (typeof project.device === 'object') {
            deviceId = project.device.id;
        } else {
            deviceId = project.device;
        }
        capabilities.push(deviceId);
        const deviceDefinition = DeviceDefinitions[deviceId];
        if (deviceDefinition !== undefined) {
            capabilities = capabilities.concat(deviceDefinition.capabilities);
            capabilities.push(deviceDefinition.deviceType);
        } else {
            const device = this.getDevice(deviceId);
            if (device !== undefined && device.capabilities) {
                capabilities = capabilities.concat(device.capabilities);
                capabilities.push(device.deviceType);
            }
        }
        for (let i = 0; i < project.features.length; i += 1) {
            const feature = project.features[i];
            if (feature.capabilities) {
                for (let j = 0; j < feature.capabilities.length; j += 1) {
                    if (!capabilities.includes(feature.capabilities[j])) {
                        capabilities.push(feature.capabilities[j]);
                    }
                }
            }
        }
        if (project.runtime) {
            capabilities.push(project.runtime);
        }
        return capabilities;
    },

    getPlaformWokwiId(platformId: string): string {
        switch (platformId) {
            case TIBBO_DEVICE_ID.RPPICOW:
                return 'board-pi-pico-w';
            case TIBBO_DEVICE_ID.RPPICO:
                return 'wokwi-pi-pico';
            default:

                return '';
        }
    },

    hexToTibboColor(hex: string) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (result) {
            const tmp = {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16),
            };
            const red = tmp.r / 8;
            const green = (tmp.g / 4) << 5;
            const blue = (tmp.b / 8) << 11;
            let color = Math.floor(red + green + blue);
            if (color >= 65535) {
                color = 65535;
            }
            return `&h${color.toString(16)}`;
        }
        return null;
    },

    getAllCapabilities(): any[] {
        return ALL_CAPABILITIES;
    },

    getAllBoards(): any[] {
        return ALL_BOARDS;
    },

    getDeviceAsync(deviceId: string): Promise<Device | undefined> {
        return new Promise(async (resolve) => {
            if (DeviceDefinitions[deviceId] !== undefined
                && DeviceDefinitions[deviceId].pins !== undefined) {
                resolve(DeviceDefinitions[deviceId]);
            }
            if (process.env.BROWSER !== undefined) {
                // browser
                // get the board definition from the server
                const device = ALL_BOARDS.find(board => board.id === deviceId);
                if (!device) {
                    return resolve(undefined);
                }
                await fetch(`${device.config}`)
                    .then(res => res.json())
                    .then((board) => {
                        if (board !== undefined) {
                            DeviceDefinitions[deviceId] = {
                                ...board,
                                diagram: device.diagram,
                            };
                            DEVICE_TYPES.push(board);
                            DEVICE_PINS[deviceId] = board.pins;
                        }
                        resolve(board);
                    });
            } else {
                resolve(undefined);
            }
        });
    },

    parseSvgPins(svgSource: string): any[] {
        if (!svgSource) {
            return [];
        }
        const pins = [];
        const svg = new DOMParser().parseFromString(svgSource, 'image/svg+xml');
        const pinElements = svg.querySelectorAll('.pin');
        for (let i = 0; i < pinElements.length; i++) {
            const pin: any = pinElements[i];
            const label = pin.closest('g')?.querySelector('.pinLabel');
            pins.push({
                x: pin.x.baseVal.value,
                y: pin.y.baseVal.value,
                num: pin.getAttribute('data-pin'),
                label: label?.textContent || '',
                id: parseInt(pin.getAttribute('id').replace('pin-', ''), 10),
            });
        }
        return pins;
    },
};

export const LCD_SCREEN_WIDGET_COLUMNS = 320;
export const LCD_SCREEN_WIDGET_ROWS = 240;
export const WEB_ADMIN_WIDGET_COLUMNS = 10;
export const WEB_ADMIN_WIDGET_ROWS = 15;
export const WEB_ADMIN_GRID_WIDTH = 996;
